import { Controller } from '@hotwired/stimulus'
import debounce from 'debounce'

// Connects to data-controller="live-search"
//
// searchKeyValue lets a page with more than one independent ransack search
// give each list its own top-level query param instead of both sharing "q" --
// otherwise sorting or filtering one list would silently leak into the
// other's search. Defaults to "q" for screens with a single, ordinary ransack
// search.
export default class extends Controller {
  static targets = ['perPage', 'perPageButton', 'sortingFields']
  static values = { searchKey: { type: String, default: 'q' } }

  initialize () {
    this.filter = debounce(this.filter.bind(this), 300)
  }

  filter (event) {
    // requestSubmit() on event.target will submit the form
    event.target.form.requestSubmit()
  }

  // Updates the per_page hidden field (perPageTarget), toggles the active
  // class on the clicked button, and submits -- a deliberate click, so it
  // bypasses the debounced #filter above and goes straight to
  // requestSubmit(). Setting .value alone doesn't dispatch "input", the
  // only event the form is wired to submit on, so without this the list
  // stayed on the old page size until some other field changed.
  updatePerPage ({ target: clickedButton, params: { perPage } }) {
    // Remove the active class from all buttons
    this.perPageButtonTargets.forEach((button) => button.classList.remove('active'))

    // Add the active class to the clicked button
    clickedButton.classList.add('active')

    this.perPageTarget.value = perPage
    this.perPageTarget.form.requestSubmit()
  }

  // Ransack "sort_link" helper encapsulates the sorting params in the URL.
  // We need to intercept the click event and update the hidden input fields,
  // so it can work together with the other form params (eg: search criteria,
  // pagination). Otherwise, only the turbo_frame "filtered results" would be
  // updated and the sorting params would be lost.
  // To intercept the clicks, add
  //   data: { action: "click->live-search#updateSortParameters" }
  // to the links.
  updateSortParameters (event) {
    // Clear any hidden input field under sortingFieldsTarget
    this.sortingFieldsTarget.innerHTML = ''

    // For each sorting parameter in url ([:q][:s]), create a hidden input field
    const sortParams = this.extractSortParams(event.target.href)

    sortParams.forEach((sortParam) => {
      const input = document.createElement('input')
      input.type = 'hidden'
      input.name = `${this.searchKeyValue}[s][]`
      input.value = sortParam
      this.sortingFieldsTarget.appendChild(input)
    })
  }

  extractSortParams (url) {
    const queryParamsString = url.split('?')[1]
    const searchParams = new URLSearchParams(queryParamsString)

    // Returns in the format: ["name asc", "id desc"]
    return searchParams.getAll(`${this.searchKeyValue}[s][]`)
  }
}
